import type { Observation } from "@medplum/fhirtypes";

import { format, isValid } from "date-fns";

import type { LaborAmendDisplayDetail } from "@/encounters/components/EncounterLabor/types/labor-amend";
import { getLocalDateString, localTimeFromISO } from "@/utils/datetime";
import { isSignatureInitialsLinkId } from "@/utils/questionnaires";

export type { LaborAmendDisplayDetail };

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  romDate: "Date",
  romTime: "Time",
  birthingHeadDateTime: "Head delivered",
  birthingBodyDateTime: "Body delivered",
  shoulderDystociaBirth: "Birth",
  shoulderDystociaBirthNotes: "Notes",
  shoulderDystociaNotes: "Notes",
  shoulderDystociaDuration: "Duration",
  contractionsFrequency: "Frequency",
  contractionsDuration: "Duration",
  contractionsNotes: "Notes",
  contractionsCoping: "Coping",
  newbornVitalsHeartRate: "Heart rate",
  newbornVitalsRespiratoryRate: "Respiratory rate",
  newbornVitalsTemperatureValue: "Temperature",
  newbornVitalsTemperatureUnit: "Unit",
  newbornVitalsTemperatureMethod: "Method",
  newbornVitalsTemperatureMethodOther: "Other method",
  newbornVitalsColor: "Color",
  newbornVitalsVoid: "Void",
  newbornVitalsVoidNotes: "Void notes",
  newbornVitalsStool: "Stool",
  newbornVitalsStoolNotes: "Stool notes",
  newbornVitalsNotes: "Notes",
  newbornFeedingType: "Nursing/Formula",
  medsAndTherapiesNotes: "Notes",
  medsAndTherapiesTherapiesIVAccess: "IV access initiated",
  medsAndTherapiesTherapiesFluidVolume: "Fluid volume",
};

const FIELD_SUFFIX_LABELS: Record<string, string> = {
  FormsetForm: "Form",
  FormsetDosage: "Dosage",
  FormsetFrequency: "Frequency",
  FormsetDuration: "Duration",
  FormsetSpecialInstructions: "Special instructions",
  FormsetPRNReason: "PRN reason",
  FormsetTherapy: "Therapy",
  FormsetMedication: "Medication",
  FrequencyDurationXDays: "# of days",
  TherapiesReason: "Reason",
  TherapiesReasonOther: "Other",
};

const FORMSET_ENTRY_TITLE_KEY_PATTERN = /Formset(Medication|Therapy)$/i;

const QUESTIONNAIRE_FIELD_PREFIXES = [
  "medsAndTherapies",
  "newbornMedsAndTherapies",
  "postpartumMedicationAndTherapies",
  "shoulderDystocia",
  "contractions",
  "cervicalExam",
  "cervical",
  "laborVitals",
  "fetalHeartRate",
  "fhr",
  "labor",
  "rom",
  "birthing",
  "resuscitation",
  "newbornVitals",
  "newborn",
  "clientCordCutClamped",
  "client",
  "placenta",
  "clientPosition",
  "clientInputOutput",
  "clientAssessmentNote",
  "newbornApgar",
  "newbornLocation",
  "newbornFirstLatch",
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCodingLike(value: unknown): value is { display?: string; code?: string } {
  return isRecord(value) && ("display" in value || "code" in value);
}

function tryParseValueSetCheckboxSelection(
  value: string,
): { display?: string; code?: string } | undefined {
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (isCodingLike(parsed)) {
      return parsed;
    }
    if (isRecord(parsed) && isCodingLike(parsed.valueCoding)) {
      return parsed.valueCoding;
    }
    if (isRecord(parsed) && typeof parsed.display === "string") {
      return {
        display: parsed.display,
        code: typeof parsed.code === "string" ? parsed.code : undefined,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function formatCodingLikeDisplay(value: { display?: string; code?: string }): string | undefined {
  const codingDisplay = value.display ?? value.code;
  return codingDisplay && !UUID_PATTERN.test(codingDisplay) ? codingDisplay : undefined;
}

function shouldSkipFieldKey(key: string): boolean {
  return isSignatureInitialsLinkId(key) || /FormsetCode$/i.test(key);
}

function isFormsetContainerKey(key: string): boolean {
  return /Formset$/i.test(key) && !/FormsetCode$/i.test(key);
}

function isFormsetEntriesRecord(value: unknown): value is Record<string, Record<string, unknown>> {
  if (!isRecord(value)) {
    return false;
  }
  const entries = Object.values(value);
  if (entries.length === 0) {
    return false;
  }
  return entries.every((entry) => isRecord(entry));
}

function isFormsetEntryTitleKey(key: string): boolean {
  return FORMSET_ENTRY_TITLE_KEY_PATTERN.test(key);
}

function getFormsetEntryTitleFieldKey(entry: Record<string, unknown>): string | undefined {
  return Object.keys(entry).find(
    (key) => isFormsetEntryTitleKey(key) && formatLeafValue(entry[key]) !== undefined,
  );
}

/**
 * Converts a normalized form field key into a short display label for amend pending lists.
 */
export function humanizeFormFieldLabel(key: string): string {
  const override = FIELD_LABEL_OVERRIDES[key];
  if (override) {
    return override;
  }

  if (key.endsWith("Date")) {
    return "Date";
  }
  if (key.endsWith("Time")) {
    return "Time";
  }

  for (const [suffix, label] of Object.entries(FIELD_SUFFIX_LABELS)) {
    if (key.endsWith(suffix)) {
      return label;
    }
  }

  let remainder = key;
  for (const prefix of QUESTIONNAIRE_FIELD_PREFIXES) {
    if (remainder.startsWith(prefix) && remainder.length > prefix.length) {
      const nextChar = remainder[prefix.length];
      if (nextChar && nextChar === nextChar.toUpperCase()) {
        remainder = remainder.slice(prefix.length);
        break;
      }
    }
  }

  if (!remainder) {
    remainder = key;
  }

  const withoutFormset = remainder.replace(/Formset/gi, "");
  const withoutDateTimeSuffix = withoutFormset.replace(/DateTime$/i, "");

  const spaced = withoutDateTimeSuffix
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  if (!spaced) {
    return key;
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatLeafValue(value: unknown): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (value instanceof Date) {
    return isValid(value) ? format(value, "MM/dd/yyyy") : undefined;
  }
  if (isCodingLike(value)) {
    return formatCodingLikeDisplay(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatLeafValue(item))
      .filter((part): part is string => Boolean(part));
    return parts.length ? parts.join(", ") : undefined;
  }
  if (isRecord(value)) {
    return undefined;
  }

  if (typeof value === "string") {
    const parsedSelection = tryParseValueSetCheckboxSelection(value);
    if (parsedSelection) {
      return formatCodingLikeDisplay(parsedSelection);
    }
  }

  const display = String(value).trim();
  if (!display || UUID_PATTERN.test(display)) {
    return undefined;
  }
  if (display.length > 120) {
    return undefined;
  }
  return display;
}

interface FlattenedFormField {
  linkId: string;
  value: unknown;
}

function appendDateTimeGroupLeaves(
  leaves: FlattenedFormField[],
  value: Record<string, unknown>,
): void {
  const dateKey = Object.keys(value).find((candidate) => candidate.endsWith("Date"));
  const timeKey = Object.keys(value).find((candidate) => candidate.endsWith("Time"));
  if (dateKey && formatLeafValue(value[dateKey])) {
    leaves.push({ linkId: dateKey, value: value[dateKey] });
  }
  if (timeKey && formatLeafValue(value[timeKey])) {
    leaves.push({ linkId: timeKey, value: value[timeKey] });
  }
}

function collectNormalizedFormLeavesFromRecord(
  values: Record<string, unknown>,
  options?: { excludeFieldKey?: string },
): FlattenedFormField[] {
  const leaves: FlattenedFormField[] = [];

  for (const [key, value] of Object.entries(values)) {
    if (shouldSkipFieldKey(key) || key === options?.excludeFieldKey) {
      continue;
    }

    if (key.endsWith("DateTime") && isRecord(value)) {
      appendDateTimeGroupLeaves(leaves, value);
      continue;
    }

    if (isCodingLike(value)) {
      if (formatLeafValue(value)) {
        leaves.push({ linkId: key, value });
      }
      continue;
    }

    if (isRecord(value)) {
      leaves.push(...collectNormalizedFormLeavesFromRecord(value));
      continue;
    }

    if (formatLeafValue(value)) {
      leaves.push({ linkId: key, value });
    }
  }

  return leaves;
}

/**
 * Walks nested questionnaire form values (group → fields) into displayable leaf rows.
 */
export function flattenNormalizedFormValues(
  normalizedValues: Record<string, unknown>,
): FlattenedFormField[] {
  return collectNormalizedFormLeavesFromRecord(normalizedValues);
}

function buildAmendDisplayDetailsFromValues(
  normalizedValues: Record<string, unknown>,
  maxFields: number,
): LaborAmendDisplayDetail[] {
  const details: LaborAmendDisplayDetail[] = [];

  const pushDetail = (detail: LaborAmendDisplayDetail) => {
    if (details.length < maxFields) {
      details.push(detail);
    }
  };

  for (const [key, value] of Object.entries(normalizedValues)) {
    if (details.length >= maxFields) {
      break;
    }
    if (shouldSkipFieldKey(key)) {
      continue;
    }

    if (key.endsWith("DateTime") && isRecord(value)) {
      for (const { linkId, value: fieldValue } of collectNormalizedFormLeavesFromRecord({
        [key]: value,
      })) {
        const formattedValue = formatLeafValue(fieldValue);
        if (formattedValue) {
          pushDetail({
            label: humanizeFormFieldLabel(linkId),
            value: formattedValue,
          });
        }
      }
      continue;
    }

    if (isFormsetContainerKey(key) && isFormsetEntriesRecord(value)) {
      for (const entry of Object.values(value)) {
        if (details.length >= maxFields) {
          break;
        }
        const titleFieldKey = getFormsetEntryTitleFieldKey(entry);
        const title = titleFieldKey ? formatLeafValue(entry[titleFieldKey]) : undefined;
        if (title) {
          pushDetail({ label: title, value: "", isGroupHeading: true });
        }
        for (const { linkId, value: fieldValue } of collectNormalizedFormLeavesFromRecord(entry, {
          excludeFieldKey: titleFieldKey,
        })) {
          const formattedValue = formatLeafValue(fieldValue);
          if (!formattedValue) {
            continue;
          }
          pushDetail({
            label: humanizeFormFieldLabel(linkId),
            value: formattedValue,
            indent: title ? 1 : 0,
          });
        }
      }
      continue;
    }

    if (isCodingLike(value)) {
      const formattedValue = formatLeafValue(value);
      if (formattedValue) {
        pushDetail({ label: humanizeFormFieldLabel(key), value: formattedValue });
      }
      continue;
    }

    if (isRecord(value)) {
      const nestedDetails = buildAmendDisplayDetailsFromValues(value, maxFields - details.length);
      details.push(...nestedDetails);
      continue;
    }

    const formattedValue = formatLeafValue(value);
    if (formattedValue) {
      pushDetail({ label: humanizeFormFieldLabel(key), value: formattedValue });
    }
  }

  return details;
}

/**
 * Builds structured label/value rows for amend-mode pending change lists.
 */
export function formatNormalizedFormValuesForAmendDisplay(
  normalizedValues: Record<string, unknown>,
  maxFields = 30,
): LaborAmendDisplayDetail[] {
  const details = buildAmendDisplayDetailsFromValues(normalizedValues, maxFields);
  return details.length ? details : [{ label: "Status", value: "Values updated" }];
}

/**
 * Parses a legacy subtitle string (`Label: value · Label: value`) into display rows.
 */
export function parseAmendDisplaySubtitle(subtitle: string): LaborAmendDisplayDetail[] {
  return subtitle
    .split(" · ")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separatorIndex = part.indexOf(": ");
      if (separatorIndex === -1) {
        return { label: part, value: "" };
      }
      return {
        label: part.slice(0, separatorIndex),
        value: part.slice(separatorIndex + 2),
      };
    });
}

export function getAmendDisplayDetails(entry: {
  details?: LaborAmendDisplayDetail[];
  subtitle?: string;
}): LaborAmendDisplayDetail[] {
  if (entry.details?.length) {
    return entry.details;
  }
  if (entry.subtitle) {
    return parseAmendDisplaySubtitle(entry.subtitle);
  }
  return [];
}

/**
 * Returns the flowsheet form label shown on labor rows (Observation code display).
 */
export function getLaborFlowsheetRowDisplayName(observation: Observation): string {
  const display = observation.code?.coding?.[0]?.display;
  if (display) return display;
  return observation.code?.text ?? "Flowsheet row";
}

/**
 * Formats a labor row timestamp for amend pending banners (date + time, flowsheet-local).
 */
export function formatLaborFlowsheetRowTimestampForAmendDisplay(observation: Observation): string {
  const effectiveDateTime = observation.effectiveDateTime;
  if (!effectiveDateTime) return "Unknown time";

  const date = getLocalDateString(effectiveDateTime);
  const time = localTimeFromISO(effectiveDateTime);
  if (date && time) return `${date} ${time}`;
  if (time) return time;
  return "Unknown time";
}

export interface LaborStageMoveAmendDisplayOptions {
  fromStageLabel?: string;
  targetStageLabel: string;
}

/**
 * Builds pending-banner details for a deferred labor-stage row move.
 */
export function buildLaborStageMovePendingDisplayDetails(
  observation: Observation,
  options: LaborStageMoveAmendDisplayOptions,
): LaborAmendDisplayDetail[] {
  const details: LaborAmendDisplayDetail[] = [
    { label: "Time", value: formatLaborFlowsheetRowTimestampForAmendDisplay(observation) },
  ];

  if (options.fromStageLabel) {
    details.push({ label: "From", value: options.fromStageLabel });
  }

  details.push({ label: "To", value: options.targetStageLabel });
  return details;
}
